/**
 * `SDKProvider` — top-level SDK runtime composition.
 *
 * Responsibilities:
 *   1. Own a `QueryClient` (constructed once via `useState` so React strict
 *      mode + reloads don't tear it down) and wrap children in TanStack's
 *      `QueryClientProvider`.
 *   2. Construct the per-provider runtime instances (inflightMap,
 *      reverseIndex, tabNonce, persister) and expose via
 *      `SdkRuntimeContext`.
 *   3. Wire realtime subscription effects from the minted JWT's `allow_sub`
 *      roots. JetStream roots and core-only subjects use separate subscribers.
 *
 * What it does NOT own:
 *   - The `SubjectRegistry` — that's `globalThis.__morfi_registry`,
 *     deliberately a singleton so HMR survives. See SSR-shield notes
 *     in `server.ts`.
 *   - Codegen-emitted routing-table or hook specs — passed in as opts.
 *
 * The web persister uses `createAsyncStoragePersister` against
 * `window.localStorage` (the deprecated `createSyncStoragePersister` is
 * being removed in the next TanStack major; the async persister accepts
 * a sync `Storage` adapter just fine).
 */
'use client';

import { jsx as _jsx } from "react/jsx-runtime";
import { SDK_ANON_ALLOW_SUBJECTS } from '@morfi/nats-client';
import { wsconnect } from '@nats-io/nats-core';
import { dehydrate, hashKey, onlineManager, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { installFetchShim } from '../api/fetch-shim.js';
import { DEFAULT_BLOCKHASH_RESOLVER_MODE } from '../blockchain/blockhash/index.js';
import { BLOCKMETA_CONFIRMED_SUBJECT } from '../blockchain/blockmeta/index.js';
import { openCacheBroadcast } from '../cache/broadcast/broadcast-cache-sync.js';
import { makeBroadcastQueue } from '../cache/broadcast/broadcast-queue.js';
import { installInflightSentinels, installPausedMutationSentinels } from '../cache/persist/hydrate-sentinels.js';
import { currentSchemaByte, excludeNonpersistentQueriesFromPersistedClient, isPersistedClientUsable, shouldDehydrateQuery } from '../cache/persist/persister.js';
import { recordSessionFetchEvent } from '../cache/persist/session-fetch.js';
import { applySdkDefaults } from '../cache/query-defaults.js';
import { createUnifiedQueryCacheListener } from '../cache/reverse-index/query-cache-listener.js';
import { createReverseIndex } from '../cache/reverse-index/reverse-index.js';
import { notePollResponse, resetPollState } from '../connectivity/degraded/degraded-poll-fallback.js';
import { installDegradedPollResume } from '../connectivity/degraded/install-poll-resume.js';
import { getSnapshot as getConnectivitySnapshot, subscribe as subscribeConnectivity } from '../connectivity/fsm/state-machine.js';
import { installNatsHeartbeat } from '../connectivity/lifecycle/nats-heartbeat.js';
import { handlePageShow, handleVisibilityChange, pingAndVerify } from '../connectivity/lifecycle/pings.js';
import { installConnectivityOrchestrator } from '../connectivity/orchestration/orchestrator.js';
import { shouldRecoveryRefetchRealtimeQuery } from '../connectivity/reconnect/fan-out.js';
import { refetchRecoverySnapshots } from '../connectivity/reconnect/refetch-snapshots.js';
import { createRemintReconnect, isSocketLive } from '../connectivity/reconnect/remint-reconnect.js';
import { reportConnectHealth, reportWireHealth } from '../connectivity/signals/connect-health.js';
import { installOnlineOfflineHandler } from '../connectivity/signals/online-offline.js';
import { probeRestConnectivity } from '../connectivity/signals/probe-rest.js';
import { getActiveQueryClient, setActiveQueryClient } from '../core/active-query-client.js';
import { RegistryContext } from '../core/context/RegistryContext.js';
import { installConvictionProjector } from '../core/conviction-projector.js';
import { createEventDispatcher } from '../core/dispatch/createEventDispatcher.js';
import { isConvictionProjectionWrite } from '../core/internal/conviction-projection-write.js';
import { activateViewerScope, hashViewerScope } from '../core/internal/viewer-overlay.js';
import { installPersistenceSentinels } from '../core/persistence-sentinels.js';
import { markNextWrite } from '../devtool/channels/cache-write.js';
import { sdkEvents } from '../devtool/events.js';
import { installDevtool } from '../devtool/install.js';
import { wrapStatusForOutageSim } from '../devtool/nats-outage-sim.js';
import { ANON_IDENTITY, deriveAuthIdentity, planIdentityFlip, shouldReuseConnectedCredential } from '../nats/auth/auth-identity.js';
import { prepareIdentityFlipQueries, recoverIdentityFlipQueries } from '../nats/auth/identity-flip-query-recovery.js';
import { initialMint } from '../nats/auth/initial-mint.js';
import { getJwtStore } from '../nats/auth/jwt-store.js';
import { preReplaceCleanup } from '../nats/auth/pre-replace-cleanup.js';
import { createRefreshRunner } from '../nats/auth/refresh-runner.js';
import { classifyConnectError } from '../nats/connect/classify-connect-error.js';
import { armColdConnectRetry, ColdConnectRetrySuperseded, cancelColdConnectRetry, deferColdConnectRetry, isColdConnectRetryArmed, retryColdConnectNow } from '../nats/connect/cold-connect-retry.js';
import { connectSingleton, resetConnectionSingleton } from '../nats/connect/connect.js';
import { markFirstPaintComplete } from '../nats/connect/first-paint-gate.js';
import { NATS_CLIENT_GLOBAL_KEY } from '../nats/connect/globals.js';
import { markClosed, wasIntentionalClose } from '../nats/connect/intentional-close.js';
import { classifyFailureReason, describeError, reportNatsFailure, reportNatsRecovery, setNatsFailureSink } from '../nats/connect/report-nats-failure.js';
import { createContentSafetyRealtimeHandler, isContentSafetySubject } from '../nats/content-safety-realtime.js';
import { setLastSeqStorage } from '../nats/last-seq-store.js';
import { clearLiveRows } from '../nats/live-since-mount-store.js';
import { PersistenceRestoredContext } from './PersistenceRestoredContext.js';
// Per-subject routing table for dispatcher fallback. Detail-side patches
// are emitted by codegen via each spec's `applyEvent`; the list-side
// dispatcher table stays EMPTY by design — auto-patching the list cache
// on every per-entity trade event cascades parent re-renders. Per-card
// detail caches handle live values via the realtime spec; the list cache
// holds structural shape only. See `realtime.manifest.yaml` for the
// declared subjects per entity.
const EMPTY_ROUTING_TABLE = new Map();
import { createIdempotencyLRU } from '../nats/registry/idempotency-lru.js';
import { getRegistrySingleton } from '../nats/registry/subject-registry.js';
import { installOtelTelemetry } from '../telemetry/index.js';
import { defaultExtractIdsFromKey, SdkRuntimeContext } from './SdkRuntimeContext.js';
import { useBlockmetaFeedSubscription } from './useBlockmetaFeedSubscription.js';
import { preloadJetStream, useNatsSubscription } from './useNatsSubscription.js';
const EMPTY_JWT = {
  jwt: '',
  allow_sub: [],
  expires_at: 0
};
/**
 * Default anon JWT — used when no `jwt` prop is supplied. `allow_sub`
 * is sourced from `nats.manifest.yml` via codegen — the channel grammar
 * has exactly one source of truth in the package. Production apps
 * replace this with an authed JWT minted from their backend auth flow.
 */
const ANON_JWT = {
  jwt: 'anon',
  allow_sub: SDK_ANON_ALLOW_SUBJECTS,
  expires_at: Date.now() + 24 * 60 * 60 * 1000
};
export function filterCoreOnlySubjectsFromJetStreamJwt(jwt) {
  if (!jwt.allow_sub.includes(BLOCKMETA_CONFIRMED_SUBJECT)) return jwt;
  return {
    ...jwt,
    allow_sub: jwt.allow_sub.filter(subject => subject !== BLOCKMETA_CONFIRMED_SUBJECT)
  };
}
/**
 * Default NATS URL — read from env at module load. Three conventions are
 * honoured so neither sandbox needs to pass it explicitly:
 *   - `NEXT_PUBLIC_MORFI_NATS_URL`  (Next.js)
 *   - `EXPO_PUBLIC_MORFI_NATS_URL`  (Expo / React Native)
 *   - `MORFI_NATS_URL`              (plain Node / framework-agnostic)
 *
 * Empty string ⇒ no connect.
 */
const DEFAULT_NATS_URL = typeof process !== 'undefined' && process.env !== undefined ? process.env.NEXT_PUBLIC_MORFI_NATS_URL ?? process.env.EXPO_PUBLIC_MORFI_NATS_URL ?? process.env.MORFI_NATS_URL ?? '' : '';
/**
 * Default API base URL — read from env at module load. Same three-way
 * convention as `DEFAULT_NATS_URL`. Empty string ⇒ no shim install,
 * `fetch` is used unmodified (web behind Next.js dev proxy works).
 */
const DEFAULT_API_URL = typeof process !== 'undefined' && process.env !== undefined ? process.env.NEXT_PUBLIC_MORFI_API_URL ?? process.env.EXPO_PUBLIC_API_URL_LOCAL ?? process.env.MORFI_API_URL ?? '' : '';
/**
 * Default API key — read from env at module load. Inserted into the
 * `X-API-Key` header on every fetch when the shim is active.
 */
const DEFAULT_API_KEY = typeof process !== 'undefined' && process.env !== undefined ? process.env.NEXT_PUBLIC_MORFI_API_KEY ?? process.env.EXPO_PUBLIC_API_KEY_LOCAL ?? process.env.MORFI_API_KEY ?? '' : '';
let retryOwnerSeq = 0;
/** Token identifying the provider mount that armed a cold-connect ladder. */
function nextRetryOwner() {
  retryOwnerSeq += 1;
  return retryOwnerSeq;
}
/** A failure that means the credential (not the transport) must change before the next attempt. */
function credentialWasRefused(reason) {
  return reason === 'auth-rejected' || reason === 'mint-rejected' || reason === 'mint-failed';
}
const _NOOP_DISPATCHER = () => {};
const PERSISTER_MAX_AGE_MS = 24 * 60 * 60 * 1000; // 24h — restore drops blobs older than this
const QC_GC_TIME_MS = 25 * 60 * 60 * 1000; // gcTime ≥ maxAge or persisted state GC's before restore
function defaultQueryClient() {
  // Server-safe: this initializer runs during SSR pre-render. The
  // SDK-specific defaults (applySdkDefaults) are client-only — wired
  // from the mount effect below, never here. Spec L1296.
  //
  // The three booleans + `retry: 1` close the first-paint window:
  // descendant observers attached during the initial commit (before
  // `applySdkDefaults` fires in the mount effect) would otherwise inherit
  // TanStack's hardcoded defaults (`retry: 3`, `refetchOnWindowFocus: true`,
  // `refetchOnReconnect: true`). That matters for consumer-owned queries
  // that share this QueryClient — e.g. on-chain RPC reads — which can hit
  // 3-retry storms on a flaky first call. The booleans match applySdkDefaults's
  // end state so there is no semantic flip post-effect; `retry: 1` is
  // a conservative startup-phase value that the mount effect replaces
  // with the smarter `retryPolicy` function.
  //
  // NOT set here: `refetchOnMount`. The SDK uses per-hook
  // `realtimeRefetchOnMount` logic; a global default would break it.
  return new QueryClient({
    defaultOptions: {
      queries: {
        gcTime: QC_GC_TIME_MS,
        staleTime: 60_000,
        refetchOnReconnect: false,
        refetchOnWindowFocus: false,
        retry: 1
      }
    }
  });
}
export function SDKProvider(props) {
  const {
    adapter,
    natsUrl = DEFAULT_NATS_URL,
    apiUrl = DEFAULT_API_URL,
    apiHeaders,
    apiKey = DEFAULT_API_KEY,
    client: clientOverride = null,
    jwt: jwtProp = null,
    resolveBearer,
    identityKey,
    viewerScopeKey,
    networkScope = 'unbound',
    onMessage: onMessageOverride,
    queryClient: ownerQc,
    persister: ownerPersister,
    extractIdsFromKey = defaultExtractIdsFromKey,
    telemetry,
    onConnectError,
    onNatsReady,
    blockhashResolverMode = DEFAULT_BLOCKHASH_RESOLVER_MODE,
    children
  } = props;
  const authIdentity = identityKey !== undefined ? identityKey === null || identityKey === '' || identityKey === 'anon' ? ANON_IDENTITY : `user:${identityKey}` : deriveAuthIdentity(jwtProp);
  const viewerScope = hashViewerScope(viewerScopeKey ?? authIdentity);
  // ── Fetch shim ────────────────────────────────────────────────────────
  // CRITICAL TIMING — installed SYNCHRONOUSLY during render, not in a
  // `useEffect`. Child observers schedule their first fetch via
  // microtasks queued from observer-mount effects; those effects run
  // bottom-up (children first, then this provider's effect). If we
  // installed the shim in `useEffect` here, the very first
  // `fetch('/v1/markets')` would beat us to the wire and use the
  // unwrapped native fetch — same race the RN sandbox had to work
  // around with a module-load `ensureFetchPatched()`.
  //
  // `installFetchShim` is idempotent (same config → no-op on second
  // call), so React strict-mode's render-double-invoke is safe.
  // Returned uninstall is intentionally ignored — the shim lives for
  // the component's lifetime (typically the app's), and HMR/repeat
  // installs unwrap previous shims before stacking.
  useMemo(() => {
    const headers = {
      ...(apiHeaders ?? {})
    };
    if (apiKey !== '' && headers['X-API-Key'] === undefined) {
      headers['X-API-Key'] = apiKey;
    }
    installFetchShim({
      apiUrl,
      apiHeaders: headers
    });
    return null;
  }, [apiUrl, apiHeaders, apiKey]);
  // ── Auto-managed NATS connection (connectSingleton-owned) ─────────────
  // When the consumer passes a pre-built `client`, use it. Otherwise the
  // SDK owns the connection via the race-guarded `connectSingleton`
  // facade: anon callers self-mint via `initialMint`; an app-minted
  // authed JWT is seeded into the jwt-store and connected directly. The
  // connection is a process-global singleton (pinned on globalThis) so
  // route remounts / StrictMode / HMR reuse the warm WS — it is torn down
  // only by the auth-flip path (preReplaceCleanup) or full page unload,
  // NOT on provider unmount.
  const [autoClient, setAutoClient] = useState(null);
  // Mount-time JWT capture: the bootstrap branch (authed-vs-anon) is
  // decided once. Subsequent identity changes are handled by the
  // auth-flip effect (next slice), not by re-running bootstrap.
  const [bootJwt] = useState(() => jwtProp);
  // QueryClient: construct once per provider mount, OR accept consumer's.
  // `useState` initialiser pattern survives StrictMode + reloads
  // correctly. Declared BEFORE the bootstrap effect because that
  // effect's dep list references `qc.refetchQueries` (the refresh
  // runner's reconcile-recovery closure) — it must be in scope.
  const [qc] = useState(() => {
    const client = ownerQc ?? defaultQueryClient();
    // Register for non-React consumers (devtool / engineering surfaces that run
    // outside this provider subtree). Done in the initialiser so it is set
    // before any descendant effect runs. Cleared on unmount (see teardown).
    setActiveQueryClient(client);
    return client;
  });
  // StrictMode-safe liveness flag for the active-query-client registration.
  // The registration above lives in the `useState` initialiser, which does NOT
  // re-run on StrictMode's dev effect re-setup pass. So the unmount teardown
  // must NOT clear the global synchronously — a descendant devtool surface
  // reading `getActiveQueryClient()` on the re-setup pass (child effects run
  // before parent effects, so before the bootstrap effect could re-register)
  // would otherwise see `undefined` for the rest of the session. The teardown
  // below defers the clear to a microtask and skips it while still mounted.
  const activeClientAliveRef = useRef(true);
  // Bearer-resolver seam. Held in a ref so the consumer may pass an
  // unstable closure without churning `connectUnderIdentity` (and thus the
  // once-per-mount bootstrap effect). `stableResolveBearer` is referentially
  // pinned (empty deps) and reads the latest resolver on every mint /
  // refresh. Absent prop ⇒ resolves `null` ⇒ anon mint (unchanged default).
  const resolveBearerRef = useRef(resolveBearer ?? null);
  resolveBearerRef.current = resolveBearer ?? null;
  const stableResolveBearer = useCallback(async () => resolveBearerRef.current !== null ? resolveBearerRef.current() : null, []);
  // Host connect-failure hook, held in a ref so an unstable closure doesn't
  // churn the bootstrap effect.
  const onConnectErrorRef = useRef(onConnectError);
  onConnectErrorRef.current = onConnectError;
  const onNatsReadyRef = useRef(onNatsReady);
  onNatsReadyRef.current = onNatsReady;
  const natsStartupStartedAtRef = useRef(null);
  // Phase stamps for `onNatsReady`: mint done → socket up → wire bound.
  const natsPhaseStampsRef = useRef({
    mintDoneAt: null,
    socketAt: null
  });
  // One cold-connect ladder owner per provider mount: the ladder is cancelled
  // when this mount's connect effects clean up, never by a stale mount.
  const [retryOwner] = useState(() => nextRetryOwner());
  // Host sink for every NATS failure the SDK reports (console + bus happen
  // inside `reportNatsFailure`; this is the third leg — web RUM loggers that
  // cannot subscribe to the bus). Installed before the connect effects below.
  useEffect(() => setNatsFailureSink(report => {
    onConnectErrorRef.current?.({
      reason: report.reason,
      isAuth: report.isAuth,
      message: report.message,
      phase: report.phase,
      stage: report.stage,
      code: report.code,
      ...(report.attempt !== undefined ? {
        attempt: report.attempt
      } : {}),
      ...(report.nextRetryMs !== undefined ? {
        nextRetryMs: report.nextRetryMs
      } : {})
    });
  }), []);
  // Report a connect-path failure (mint or socket) through the single
  // reporter — console.error on every attempt, bus + host hook on attempts
  // 1, 2, 4, 8 — and drive the connectivity FSM unhealthy so
  // `useGetConnection().state` reflects DEGRADED_NO_NATS (after the 3 s
  // hysteresis) instead of staying optimistically CONNECTED while retrying.
  // Returns the classified reason so the caller can arm the matching retry.
  const reportConnectFailure = useCallback((err, phase, extra = {}) => {
    const {
      reason,
      isAuth,
      message
    } = classifyConnectError(err);
    lastConnectFailureReasonRef.current = reason;
    const stage = reason === 'mint-rejected' || reason === 'mint-failed' ? 'mint' : 'ws-connect';
    reportNatsFailure({
      phase,
      stage,
      code: stage === 'mint' ? 'NATS_MINT_FAILED' : 'NATS_CONNECT_FAILED',
      reason,
      message,
      isAuth,
      natsUrl,
      error: err,
      ...extra
    });
    reportConnectHealth(false, `connect-failed:${reason}`);
    return reason;
  }, [natsUrl]);
  // Ladder rungs report through the same path; the rung count continues
  // from the failure that armed the ladder (attempt 1).
  const onColdRetryAttemptFailed = useCallback(failure => {
    reportConnectFailure(failure.error, 'retry', {
      attempt: failure.attempt + 1,
      nextRetryMs: failure.nextRetryMs
    });
  }, [reportConnectFailure]);
  // Latest connected identity, read at reconnect time so the recovery
  // re-mints under the CURRENT identity (login/logout swaps it). Assigned
  // below once `jwt` is derived; held in a ref so it never churns a callback.
  const jwtRef = useRef(null);
  // Recovery driver — re-built below once `connectUnderIdentity` is in scope
  // (see `createRemintReconnect`). Held in a ref so the connectivity effect's
  // lifecycle handlers always read the latest closure without re-installing.
  const runReconnectWithRemintRef = useRef(async () => {});
  // The identity bridge hydrates inside this provider, so an anon→user label
  // update can arrive while the cookie-bound startup connection is still in
  // flight. The flip effect awaits this exact attempt before deciding whether
  // the already-connected credential needs replacing.
  const bootstrapConnectionRef = useRef(null);
  // The JWT actually minted + connected under. Set by `acquireConnection`
  // (below) once the mint resolves and the WS is up — it reads the live
  // `allow_sub` from the jwt-store, so the subscription effects use the real
  // minted scope rather than the anon default. `null` until the first connect
  // means zero subscriptions during the intended pre-mint window.
  const [connectedJwt, setConnectedJwt] = useState(null);
  // Connect (or re-connect) the SDK-owned NATS connection under a given
  // identity. Shared by the once-per-mount bootstrap effect AND the
  // identity-flip effect (Slice 3) so the authed-vs-anon branch lives in
  // exactly ONE place. `isCancelled` lets the caller abort a late
  // `setAutoClient` after unmount / supersession.
  // Reason of the most recent connect-path failure. A ladder rung re-mints
  // only when the credential itself was refused (auth / mint); a transport
  // failure reuses the JWT already in the store instead of minting again on
  // every rung (2 mints/min per client at the 30 s cap during an outage).
  const lastConnectFailureReasonRef = useRef(null);
  const connectUnderIdentity = useCallback(async (targetJwt, isCancelled, options = {}) => {
    natsStartupStartedAtRef.current = Date.now();
    // The authenticator closure reads the JWT cell on EVERY (re)connect
    // attempt, so a refresh / flip only updates the store — the client
    // is never reconstructed.
    const authenticator = () => getJwtStore().current()?.jwt ?? '';
    const acquireConnection = async () => {
      natsPhaseStampsRef.current.mintDoneAt = Date.now();
      const nc = await connectSingleton({
        servers: [natsUrl],
        authenticator
      }, wsconnect);
      if (isCancelled()) return;
      natsPhaseStampsRef.current.socketAt = Date.now();
      // Pin the live NC to the canonical global (same key the Reconnect
      // button + RN app-state modules read; see nats/connect/globals.ts).
      globalThis[NATS_CLIENT_GLOBAL_KEY] = nc;
      // Publish the minted JWT (jwt + `allow_sub`) for the wire-sub
      // effect. Both mint branches have populated the jwt-store before
      // `acquireConnection` runs, so `current()` is the freshly-minted
      // credential. Batched with `setAutoClient` ⇒ the wire-sub effect
      // re-runs once, with the client AND the real roots together.
      const creds = getJwtStore().current();
      setConnectedJwt(creds !== null && creds.jwt !== '' ? {
        jwt: creds.jwt,
        allow_sub: creds.allowSub ?? SDK_ANON_ALLOW_SUBJECTS,
        expires_at: 0
      } : null);
      setAutoClient(nc);
      // A live socket is up — clear the connect-bootstrap unhealthy signal so
      // the FSM (and `useGetConnection`) settles back to CONNECTED.
      reportConnectHealth(true, 'connected');
    };
    // baseUrl '' (relative `/v1/auth/nats-credentials`) — NOT apiUrl.
    // An absolute URL bypasses the fetch-shim, which rewrites + injects
    // `X-API-Key` ONLY for relative `/v1/*` (src/api/fetch-shim.ts).
    // Web: apiUrl='' + Next proxy injects the key. RN: apiUrl set →
    // the shim rewrites to apiUrl AND injects the key. An absolute
    // baseUrl skipped both → RN mint 401 (no proxy, no key).
    const mintConfig = {
      baseUrl: '',
      // Fast: a mint that fails is retried in seconds, and the cold-connect
      // ladder (30 s cap, never terminal) owns anything longer.
      retryDelaysMs: [1_000, 2_000, 4_000]
    };
    if (targetJwt !== null && targetJwt.jwt !== '' && targetJwt.jwt !== 'anon') {
      // App-owns-auth: the consumer minted an authed JWT. Seed the cell
      // (incl. `allow_sub` so the wire-sub opens the user's roots) and
      // connect — the SDK does NOT self-mint here.
      getJwtStore().set({
        jwt: targetJwt.jwt,
        kind: 'user',
        servers: [natsUrl],
        allowSub: targetJwt.allow_sub
      });
      await acquireConnection();
    } else if (options.reuseCredential === true && (getJwtStore().current()?.jwt ?? '') !== '') {
      // Transport retry: the JWT in the store is still valid (hours of
      // TTL); reconnect with it instead of minting another one.
      await acquireConnection();
    } else {
      // No app-minted JWT → SDK self-mint. `stableResolveBearer` decides
      // anon-vs-user: a non-null bearer yields a user-scoped mint, null
      // yields anon. The REAL mid-session refresh planner is armed on the
      // ladder (re-mint → jwtStore.update → nc.reconnect → reconcile) and
      // re-resolves the bearer on every refresh, so a token rotation
      // keeps the same identity scope.
      const runRefresh = createRefreshRunner({
        mintConfig,
        resolveBearer: stableResolveBearer,
        onFailure: (attempt, err_0, nextRetryMs) => {
          const {
            reason: reason_0,
            isAuth: isAuth_0
          } = classifyFailureReason(err_0);
          reportNatsFailure({
            phase: 'jwt-refresh',
            stage: 'jwt-refresh',
            code: 'NATS_JWT_REFRESH_FAILED',
            reason: reason_0,
            message: describeError(err_0),
            isAuth: isAuth_0,
            attempt,
            nextRetryMs,
            natsUrl,
            error: err_0
          });
        },
        // Reconcile-recovery when /v1/reconnect-snapshot is absent:
        // refetch the realtime queries the user is observing.
        refetchRealtime: async () => {
          await qc.refetchQueries({
            predicate: query => shouldRecoveryRefetchRealtimeQuery(qc, query),
            type: 'active'
          });
        }
      });
      await initialMint({
        mintConfig,
        realmKey: `${networkScope}\u0000${apiUrl}\u0000${natsUrl}`,
        resolveBearer: stableResolveBearer,
        acquireConnection,
        runRefresh
      });
    }
  }, [apiUrl, natsUrl, networkScope, qc, stableResolveBearer]);
  // Recovery driver for the online edge / app-resume / bfcache restore.
  // A bare `nc.reconnect()` is a no-op while the socket is down (nats-core
  // only force-disconnects an already-connected client), which left mobile
  // hanging on "Reconnecting" until a page refresh. `createRemintReconnect`
  // rebuilds a fresh connection when the socket isn't live — the in-place
  // equivalent of a refresh (re-mint + new WS + heartbeat re-install +
  // registry interest replay) — and only reconciles when it's already healthy.
  const runReconnectWithRemint = useMemo(() => createRemintReconnect({
    getLiveNc: () => globalThis[NATS_CLIENT_GLOBAL_KEY] ?? null,
    connectFresh: () => connectUnderIdentity(jwtRef.current, () => false),
    resetConnectionSingleton,
    refetchRealtime: async () => {
      // The online snapshot ladder may already be fetching these same queries.
      await qc.refetchQueries({
        predicate: query_0 => shouldRecoveryRefetchRealtimeQuery(qc, query_0),
        type: 'active'
      }, {
        cancelRefetch: false
      });
    },
    reportFailure: (err_1, cause) => reportConnectFailure(err_1 instanceof Error ? err_1 : new Error(`reconnect failed (${cause}): ${String(err_1)}`), 'retry'),
    armRetry: (reason_1, performRetry) => void armColdConnectRetry({
      reason: reason_1,
      performRetry,
      owner: retryOwner,
      opts: {
        onAttemptFailed: onColdRetryAttemptFailed
      }
    })
  }), [qc, connectUnderIdentity, reportConnectFailure, retryOwner, onColdRetryAttemptFailed]);
  runReconnectWithRemintRef.current = runReconnectWithRemint;
  // Once-per-mount connection bootstrap for the mount-time identity.
  // Subsequent identity changes are handled by the flip effect below.
  useEffect(() => {
    if (clientOverride !== null) return; // BYO connection
    if (natsUrl === '') return; // explicit REST-only mode
    let cancelled = false;
    // Release the SSR first-paint gate now the provider has committed on
    // the client — `connect()` awaits this before opening the WS so an
    // event can't land between server-dehydrate and client-hydrate.
    // Idempotent; safe under StrictMode double-invoke.
    markFirstPaintComplete();
    // Warm the JetStream chunk while the credential mints and the socket
    // opens, so bootstrap does not start with a network fetch.
    void preloadJetStream().catch(() => {});
    const bootstrapConnection = connectUnderIdentity(bootJwt, () => cancelled).catch(err_2 => {
      if (cancelled) return;
      // Arm the REAL retry first so the failure report can say when the next
      // attempt is. The ladder never ends: it re-runs the full bootstrap path
      // under the CURRENT identity (`jwtRef`, never the mount-time token) —
      // on the self-mint branch that mints a FRESH JWT before reconnecting,
      // healing stale/expired-token `Authorization Violation`s. A rung whose
      // effect is gone stops without re-arming (`ColdConnectRetrySuperseded`).
      const reason_2 = classifyConnectError(err_2).reason;
      const nextRetryMs_0 = armColdConnectRetry({
        reason: reason_2,
        owner: retryOwner,
        performRetry: async () => {
          if (cancelled) throw new ColdConnectRetrySuperseded();
          await connectUnderIdentity(jwtRef.current, () => cancelled, {
            reuseCredential: !credentialWasRefused(lastConnectFailureReasonRef.current)
          });
        },
        opts: {
          onAttemptFailed: onColdRetryAttemptFailed
        }
      });
      reportConnectFailure(err_2, 'bootstrap', {
        attempt: 1,
        nextRetryMs: nextRetryMs_0
      });
    });
    bootstrapConnectionRef.current = bootstrapConnection;
    return () => {
      // Do NOT close/reset the connection singleton on unmount — it is
      // process-global so remounts reuse the warm WS. Cancel the pending
      // setState so a late acquire after unmount is a no-op, and this
      // mount's ladder so an orphan rung cannot open a socket nobody owns.
      cancelled = true;
      cancelColdConnectRetry({
        owner: retryOwner
      });
    };
  }, [natsUrl, clientOverride, bootJwt, connectUnderIdentity, reportConnectFailure, retryOwner, onColdRetryAttemptFailed]);
  const client_0 = clientOverride ?? autoClient;
  // Anon JWT default — production apps mint authed via `jwt` prop.
  const jwt = jwtProp ?? (natsUrl !== '' ? ANON_JWT : null);
  // Keep the recovery driver minting under the live identity (login/logout).
  jwtRef.current = jwt;
  // The `VisibilityController` installation happens in the adapter's
  // construction (e.g. `createWebDefaultAdapter()` calls
  // `installWebVisibility()` internally). The web adapter installs the
  // IntersectionObserver-backed controller on the registry singleton
  // before this component mounts; the RN adapter wires its FlatList-
  // based equivalent the same way.
  // Auto-build the dispatcher when the consumer doesn't pass `onMessage`.
  // This is the "out of the box" path — incoming NATS events automatically
  // flow through:
  //   - the dispatcher routing table (empty by design — list-cache
  //     auto-patching cascades parent re-renders; detail-side patches
  //     are emitted per-spec via the codegen-emitted `applyEvent`)
  //   - the SubjectRegistry singleton (per-hook `applyEvent` callbacks)
  //   - the idempotency LRU (dedup `patch:set` redelivery)
  //
  // Consumers can still pass a custom `onMessage` to override — useful for
  // tests, telemetry wrapping, or codegen-emitted dispatchers.
  const [autoIdempotencyLru] = useState(() => createIdempotencyLRU({
    capacity: 4096,
    minRetentionMs: 90_000
  }));
  const autoDispatcher = useMemo(() => {
    if (onMessageOverride !== undefined) return onMessageOverride;
    return createEventDispatcher({
      qc,
      registry: getRegistrySingleton(qc),
      idempotencyLru: autoIdempotencyLru,
      routingTable: EMPTY_ROUTING_TABLE,
      onError: err_3 => {
        console.error('[sdk dispatch]', err_3);
      }
    });
  }, [qc, autoIdempotencyLru, onMessageOverride]);
  const contentSafetyRealtime = useMemo(() => createContentSafetyRealtimeHandler(qc), [qc]);
  // Per-provider runtime instances. Constructed once, never re-instantiated.
  // `tabNonce` defaults to 0 during SSR (deterministic so hydration matches);
  // a `useEffect` below upgrades it to a real random int after client mount.
  const [persistenceRestored, setPersistenceRestored] = useState(false);
  const [runtimeBase, setRuntimeBase] = useState(() => ({
    inflightMap: new Map(),
    reverseIndex: createReverseIndex(),
    tabNonce: 0,
    persister: ownerPersister ?? adapter.queryPersister.persister
  }));
  useEffect(() => () => {
    void qc.cancelQueries();
  }, [qc]);
  useEffect(() => {
    activateViewerScope(qc, viewerScope);
  }, [qc, viewerScope]);
  // Upgrade `tabNonce` after mount so cross-tab snapshot dedup has a real
  // per-tab id, without breaking SSR hydration (which sees the deterministic 0).
  useEffect(() => {
    setRuntimeBase(prev => prev.tabNonce === 0 ? {
      ...prev,
      tabNonce: Math.floor(Math.random() * 2 ** 31)
    } : prev);
  }, []);
  // Stabilise the message handler so changes to other props don't ripple
  // into the wire-sub effect dependency list.
  const dispatcher = useCallback((subject, data, seq) => {
    if (isContentSafetySubject(subject)) {
      void contentSafetyRealtime.handle(subject, data).catch(err_4 => {
        sdkEvents.emit('error', {
          category: 'realtime-degraded',
          code: 'CONTENT_SAFETY_REALTIME_INVALIDATION_FAILED',
          devMessage: err_4 instanceof Error ? err_4.message : String(err_4),
          subject
        });
      });
      return;
    }
    autoDispatcher(subject, data, seq);
  }, [autoDispatcher, contentSafetyRealtime]);
  // Compose the runtime value. Memo so consumers re-render only on real changes.
  const runtime = useMemo(() => ({
    adapter,
    inflightMap: runtimeBase.inflightMap,
    reverseIndex: runtimeBase.reverseIndex,
    tabNonce: runtimeBase.tabNonce,
    extractIdsFromKey,
    dispatcher,
    persister: runtimeBase.persister,
    viewerScope,
    networkScope
  }), [adapter, runtimeBase, extractIdsFromKey, dispatcher, viewerScope, networkScope]);
  // ── Auth identity-flip · Slice 3 (HIGH-severity cache-leak fix) ───────
  // On login / logout / account-switch the consumer swaps the `jwt`
  // prop. `deriveAuthIdentity` keys on the stable `allow_sub` scope so a
  // mere token refresh (same user, new token) does NOT flip. On a real
  // identity change, in strict order BEFORE reconnecting under the new
  // identity:
  //   1. preReplaceCleanup — release every per-entity Acquisition, drain
  //      the old WS, reset the connection + jwt singletons.
  //   2. Apply the planned cache strategy: soft login invalidates active
  //      viewer-sensitive queries in place; hard purge synchronously clears
  //      their data while retaining mounted observers for recovery.
  //   3. On hard purge, persister.removeClient() drops any legacy on-disk
  //      snapshot so a reload cannot restore the prior identity's cache
  //      under the new one (closes the persisted half of the leak).
  // The cache plan runs even in REST-only mode (the leak is the query cache,
  // not just NATS). `clientOverride` consumers
  // own their connection + identity lifecycle, so the SDK does not flip.
  // SDK-owns-minting: when the app passes `identityKey`, the flip keys on
  // it — the httpOnly `morfi_session` cookie is invisible to the SDK, so
  // the app's id string is the only login/logout signal. App-owns-minting
  // (or pure-anon): fall back to deriving identity from the `jwt` prop's
  // `allow_sub` scope. `identityKey` is network-agnostic by contract, so a
  // devnet↔mainnet switch never flips identity here (no spurious logout).
  const prevAuthIdentityRef = useRef(null);
  useEffect(() => {
    if (clientOverride !== null) return; // consumer-owned connection + identity
    const prev_0 = prevAuthIdentityRef.current;
    if (prev_0 === null) {
      // First commit — the bootstrap effect already connected this
      // identity; record it so only real *changes* flip.
      prevAuthIdentityRef.current = authIdentity;
      return;
    }
    if (prev_0 === authIdentity) return; // token refresh / no identity change
    prevAuthIdentityRef.current = authIdentity;
    let cancelled_0 = false;
    const kind = authIdentity === ANON_IDENTITY ? 'logout' : 'login';
    // anon→user login carries no private data to purge, so it refetches in
    // place rather than wiping the cache (which would flash every public
    // surface — most visibly the markets hero → skeleton → hero). Logout and
    // account-switch hard-purge. See `planIdentityFlip`.
    const strategy = planIdentityFlip(prev_0, authIdentity);
    void (async () => {
      let recoveryTargets = [];
      try {
        if (strategy === 'soft-login') {
          // Auth state hydrates inside SDKProvider. Let the cookie-bound
          // startup attempt reveal whether it already connected as this user
          // before treating the outer anon→user label update as a remint.
          await bootstrapConnectionRef.current;
          if (cancelled_0) return;
        }
        const liveNc = globalThis[NATS_CLIENT_GLOBAL_KEY] ?? null;
        const reuseConnectedCredential = shouldReuseConnectedCredential(prev_0, authIdentity, getJwtStore().current()?.kind ?? null, liveNc !== null && isSocketLive(liveNc));
        if (!reuseConnectedCredential) {
          await preReplaceCleanup({
            kind,
            nc: liveNc
          });
        }
        recoveryTargets = await prepareIdentityFlipQueries(qc, strategy);
        if (strategy === 'hard-purge') await runtimeBase.persister?.removeClient?.();
        if (cancelled_0) return;
        if (!reuseConnectedCredential && natsUrl !== '') {
          await connectUnderIdentity(jwt, () => cancelled_0);
        }
        if (cancelled_0) return;
        await recoverIdentityFlipQueries(qc, recoveryTargets, strategy);
      } catch (err_5) {
        if (cancelled_0) return;
        // Reconnect under the new identity failed — report + real retry
        // (re-mints on the self-mint branch), on this mount's ladder.
        const reason_3 = classifyConnectError(err_5).reason;
        const nextRetryMs_1 = armColdConnectRetry({
          reason: reason_3,
          owner: retryOwner,
          performRetry: async () => {
            if (cancelled_0) throw new ColdConnectRetrySuperseded();
            if (recoveryTargets.length === 0) {
              recoveryTargets = await prepareIdentityFlipQueries(qc, strategy);
            }
            if (strategy === 'hard-purge') await runtimeBase.persister?.removeClient?.();
            await connectUnderIdentity(jwtRef.current, () => cancelled_0);
            if (cancelled_0) return;
            await recoverIdentityFlipQueries(qc, recoveryTargets, strategy);
          },
          opts: {
            onAttemptFailed: onColdRetryAttemptFailed
          }
        });
        reportConnectFailure(err_5, 'identity-flip', {
          attempt: 1,
          nextRetryMs: nextRetryMs_1
        });
      }
    })();
    return () => {
      cancelled_0 = true;
      cancelColdConnectRetry({
        owner: retryOwner
      });
    };
  }, [authIdentity, jwt, clientOverride, natsUrl, qc, runtimeBase.persister, connectUnderIdentity, reportConnectFailure, retryOwner, onColdRetryAttemptFailed]);
  // ── Pre-client edges ─────────────────────────────────────────────────
  // Every recovery handler below is installed once a client exists. Before
  // that — a first connect that keeps failing — the cold ladder is the only
  // driver, so let the cheap edges shorten it: network back / app visible /
  // bfcache restore retry now; an RN app going to background parks the rung.
  useEffect(() => {
    if (clientOverride !== null || natsUrl === '' || client_0 !== null) return;
    const unsubNet = adapter.netInfo.addListener(online => {
      if (online && isColdConnectRetryArmed()) retryColdConnectNow();
    });
    const unsubApp = adapter.appState.addListener(ev => {
      if (ev.kind === 'visible' || ev.kind === 'bfcache-restore' || ev.kind === 'resume') {
        if (isColdConnectRetryArmed()) retryColdConnectNow();
        return;
      }
      if (ev.kind === 'hidden' && adapter.kind === 'rn') deferColdConnectRetry();
    });
    return () => {
      unsubNet();
      unsubApp();
    };
  }, [adapter, client_0, clientOverride, natsUrl]);
  // ── Permanent close watcher ──────────────────────────────────────────
  // nats-core reconnects forever after a drop, but it gives up for good after
  // two consecutive auth errors (an expired JWT once the refresh ladder
  // failed) and on explicit close. Nothing else observes that: the heartbeat
  // sees the final `disconnect`, then silence. Rebuild through the same
  // re-mint + reconnect path the online edge uses; our own drains/closes are
  // marked intentional and ignored.
  useEffect(() => {
    if (client_0 === null) return;
    let cancelled_1 = false;
    void client_0.closed().then(err_6 => {
      markClosed(client_0);
      if (cancelled_1 || wasIntentionalClose(client_0)) return;
      const pinned = globalThis[NATS_CLIENT_GLOBAL_KEY];
      if (pinned !== client_0) return;
      const cause_0 = err_6 instanceof Error ? err_6 : new Error(err_6 === undefined ? 'connection closed by nats-core' : String(err_6));
      const {
        reason: reason_4,
        isAuth: isAuth_1
      } = classifyFailureReason(cause_0);
      reportNatsFailure({
        phase: 'closed',
        stage: 'ws-connect',
        code: 'NATS_CONNECTION_CLOSED',
        reason: reason_4,
        message: describeError(cause_0),
        isAuth: isAuth_1,
        natsUrl,
        error: err_6
      });
      reportConnectHealth(false, `closed:${reason_4}`);
      void runReconnectWithRemintRef.current('closed');
    }, () => {});
    return () => {
      cancelled_1 = true;
    };
  }, [client_0, natsUrl]);
  // ── Authed mid-session token refresh · app-owns-auth ─────────────────
  // When the consumer rotates an authed JWT for the SAME user, the
  // `allow_sub` scope is unchanged → `authIdentity` is unchanged → the
  // identity-flip effect (correctly) does NOT fire, and the anon refresh
  // ladder (`createRefreshRunner`, armed only on the self-mint branch)
  // does not apply. Without this, the fresh token never reaches the
  // authenticator closure (`getJwtStore().current()`): the SDK keeps
  // presenting the expiring token and only nats-core's blind infinite
  // reconnect eventually recovers, with NO reconcile of events missed
  // across the reconnect gap. So on a same-identity authed-token change:
  // update the jwt-store, proactively `nc.reconnect()` under the fresh
  // token, then refetch realtime queries (the same reconcile-recovery
  // the refresh runner uses). The SDK does NOT self-mint here — the app
  // owns authed minting; the SDK only keeps the store + wire current.
  const authedToken = jwtProp !== null && jwtProp.jwt !== '' && jwtProp.jwt !== 'anon' ? jwtProp.jwt : null;
  const prevAuthedRef = useRef(null);
  useEffect(() => {
    if (clientOverride !== null) return; // consumer owns the connection
    if (authedToken === null) {
      prevAuthedRef.current = null; // anon / no authed token
      return;
    }
    const prev_1 = prevAuthedRef.current;
    prevAuthedRef.current = {
      token: authedToken,
      identity: authIdentity
    };
    if (prev_1 === null) return; // first authed token — bootstrap/flip set the store
    if (prev_1.identity !== authIdentity) return; // real identity change — flip effect owns it
    if (prev_1.token === authedToken) return; // no rotation
    // Same identity, token rotated → app-owns-auth mid-session refresh.
    getJwtStore().update(authedToken);
    let cancelled_2 = false;
    void (async () => {
      try {
        const nc_0 = globalThis[NATS_CLIENT_GLOBAL_KEY] ?? null;
        if (nc_0 !== null) await nc_0.reconnect();
        if (cancelled_2) return;
        await qc.refetchQueries({
          predicate: query_1 => shouldRecoveryRefetchRealtimeQuery(qc, query_1),
          type: 'active'
        });
      } catch (err_7) {
        console.error('[morfi-sdk] authed token-refresh reconnect failed:', err_7);
      }
    })();
    return () => {
      cancelled_2 = true;
    };
  }, [authedToken, authIdentity, clientOverride, qc]);
  // Wire the NATS subscription off the *minted* JWT (`connectedJwt`) — its
  // `allow_sub` is the real server-authorized scope, not the anon `jwt` prop
  // default. `connectedJwt` is `null` until the first mint+connect resolves,
  // so subscription effects see `EMPTY_JWT` and open zero subscriptions during
  // the intended pre-mint window. Effect re-fires only when `allow_sub` changes;
  // a login/logout flip re-mints and re-publishes `connectedJwt`.
  //
  // When the FilterManager finishes bootstrap, install its add/remove on
  // the registry singleton so subject acquisitions narrow the wire
  // filter_subjects list. Idempotent — the registry holds whichever set
  // of callbacks lands first; subsequent FilterManagers (HMR / JWT
  // refresh) overwrite.
  const subscriptionJwt = connectedJwt ?? (clientOverride !== null ? jwt : null) ?? EMPTY_JWT;
  const jetstreamJwt = useMemo(() => filterCoreOnlySubjectsFromJetStreamJwt(subscriptionJwt), [subscriptionJwt]);
  const jetstreamRootsKey = jetstreamJwt.allow_sub.join('|');
  // Set this before descendants render: their passive effects acquire
  // realtime subjects before this provider's effects run, so an effect here
  // would briefly describe a connected provider as REST-only.
  useMemo(() => {
    // An authorization-root replacement rebuilds the FilterManager even when
    // the transport client itself is unchanged.
    void jetstreamRootsKey;
    const registry = getRegistrySingleton(qc);
    if (client_0 === null && natsUrl === '') {
      registry.setWireInterestUnavailable();
      return null;
    }
    registry.setWireInterestPending();
    return null;
  }, [client_0, jetstreamRootsKey, natsUrl, qc]);
  // Failed bootstrap attempts for the current client, for the recovery log.
  const wireBootstrapFailuresRef = useRef(null);
  useNatsSubscription(client_0, jetstreamJwt, dispatcher, runtimeBase.tabNonce, {
    onReady: fm => {
      getRegistrySingleton(qc).setWireInterestCallbacks({
        requestInterest: subject_0 => fm.add(subject_0),
        releaseInterest: subject_1 => fm.remove(subject_1),
        awaitInterest: subjects => fm.awaitConcreteInterest(subjects),
        hasInterest: subjects_0 => fm.hasConcreteInterest(subjects_0),
        hasCoverage: subjects_1 => fm.hasPollingCoverage(subjects_1),
        replayInterest: (subjects_2, startSequence) => fm.replayBoundedFromSequence(subjects_2, startSequence)
      });
      reportWireHealth(true, 'wire-ready');
      const failures = wireBootstrapFailuresRef.current;
      if (failures !== null) {
        wireBootstrapFailuresRef.current = null;
        reportNatsRecovery({
          stage: 'jetstream-bootstrap',
          code: 'NATS_WIRE_RECOVERED',
          attempts: failures.attempts + 1,
          downtimeMs: Math.max(0, Date.now() - failures.since)
        });
      }
      const startedAt = natsStartupStartedAtRef.current;
      if (startedAt !== null) {
        natsStartupStartedAtRef.current = null;
        const now = Date.now();
        const stamps = natsPhaseStampsRef.current;
        const mintDoneAt = stamps.mintDoneAt ?? startedAt;
        const socketAt = stamps.socketAt ?? mintDoneAt;
        onNatsReadyRef.current?.({
          credentialKind: getJwtStore().current()?.kind === 'user' ? 'user' : 'anon',
          durationMs: Math.max(0, now - startedAt),
          phases: {
            mintMs: Math.max(0, mintDoneAt - startedAt),
            connectMs: Math.max(0, socketAt - mintDoneAt),
            bootstrapMs: Math.max(0, now - socketAt)
          }
        });
      }
    },
    onAttemptFailed: failure_0 => {
      const {
        reason: reason_5,
        isAuth: isAuth_2
      } = classifyFailureReason(failure_0.error);
      const prior = wireBootstrapFailuresRef.current;
      wireBootstrapFailuresRef.current = {
        attempts: (prior?.attempts ?? 0) + 1,
        since: prior?.since ?? Date.now()
      };
      reportNatsFailure({
        phase: failure_0.round === 1 ? 'bootstrap' : 'retry',
        stage: 'jetstream-bootstrap',
        code: 'NATS_WIRE_BOOTSTRAP_FAILED',
        reason: reason_5,
        message: describeError(failure_0.error),
        isAuth: isAuth_2,
        attempt: failure_0.attempt,
        nextRetryMs: failure_0.nextRetryMs,
        natsUrl,
        error: failure_0.error
      });
      // A live socket with no wire is DEGRADED_NO_NATS: realtime is not
      // flowing, so the FSM turns on polling (after the 3 s hysteresis).
      reportWireHealth(false, `wire-bootstrap-failed:${reason_5}`);
    },
    onBootstrapFailed: () => {
      // The first round failed: flip the registry REST-only so parked
      // wire-interest waiters resolve and gated queries fetch instead of
      // waiting on a wire that is not coming yet. The loop keeps trying in
      // the background; a later success re-enables the wire via
      // `setWireInterestCallbacks`, which re-runs every mounted handoff.
      getRegistrySingleton(qc).setWireInterestUnavailable();
    },
    getInitialInterest: () => getRegistrySingleton(qc).collectActiveWireInterest(),
    onConsumerRecovered: () => getRegistrySingleton(qc).notifyWireConsumerRecreated(),
    appState: adapter.appState
  });
  useBlockmetaFeedSubscription(client_0, subscriptionJwt, blockhashResolverMode, adapter.appState, networkScope);
  // Round B · Boot the cache + persistence runtime exactly once per mount.
  // Each install returns a teardown; the cleanup function composes them in
  // reverse install order. Persister-dependent installs gate on `persister`.
  useEffect(() => {
    const teardowns = [];
    // Wire the per-subject `lastSeq` store to the adapter's sync storage.
    // Web persists across reloads via `localStorage`; RN's MMKV-backed
    // sync storage works the same. SSR / no-storage adapters degrade
    // gracefully — the store keeps lastSeqs in-memory only.
    setLastSeqStorage(adapter.storage.sync);
    teardowns.push(() => setLastSeqStorage(null));
    // FR3 fix #1 — pin SDK default options (no reconnect/focus refetch
    // storms; category-driven retry policy; throwOnError predicate that
    // keeps stale data on transient errors). Spec L1296. Client-only —
    // applySdkDefaults asserts non-server context.
    applySdkDefaults(qc);
    // FR3 fix #3 — unified QueryCache.subscribe owner. EXACTLY ONE
    // subscribe call per QueryClient lifetime. The unified listener
    // dispatches 'added'/'updated'/'removed' to pendingListKeys BEFORE
    // reverseIndex (spec §L1779 in-flight list race ordering). For Stage 1
    // the pendingListKeys handlers are no-ops — the registry will be
    // wired in Stage 3 when codegen emits list specs with listCovers.
    const reverseIndexHandlers = {
      onAdded: () => {},
      onUpdated: () => {},
      onRemoved: event => {
        const ids = extractIdsFromKey(event.query.queryKey);
        for (const id of ids) {
          runtimeBase.reverseIndex.removeKeyForId(id, event.query.queryKey);
        }
      }
    };
    const pendingListHandlers = {
      onAdded: () => {},
      onUpdated: () => {},
      onRemoved: () => {}
    };
    // FR10 — cross-tab cache:write sync. The `broadcast` slot returns a
    // `supported: false` handle on platforms without a BroadcastChannel
    // (RN, older Safari); `openCacheBroadcast` then no-ops and we pass NO
    // `broadcastOut`, so the unified listener does zero extra work there.
    // Inbound (peer → this tab) lives in `openCacheBroadcast`; outbound
    // (this tab → peers) is the `broadcastOut` handler below, fed by the
    // ONE unified QueryCache subscription. `isApplyingRemote()` breaks the
    // echo loop: a write we applied FROM a peer is never re-broadcast.
    const cacheHandle = adapter.broadcast.openBroadcast(`morfi-sdk:cache:write:${JSON.stringify([apiUrl, networkScope, currentSchemaByte()])}`);
    const cacheBinding = openCacheBroadcast({
      qc,
      handle: cacheHandle,
      tabNonce: runtimeBase.tabNonce,
      // Attribute the inbound peer write to source:'broadcast' so the
      // devtool (`perf.cacheWrites.byBroadcast`, timeline source) sees it.
      markWrite: key => markNextWrite(qc, hashKey(key), 'broadcast')
    });
    const broadcastQueue = makeBroadcastQueue({
      postMessage: m => cacheBinding.post(m)
    }, runtimeBase.tabNonce);
    const broadcastOut = cacheHandle.supported ? {
      onUpdated: event_0 => {
        if (cacheBinding.isApplyingRemote() || isConvictionProjectionWrite(qc, event_0.query.queryHash)) return;
        const {
          state,
          queryKey
        } = event_0.query;
        if (state.status !== 'success' || state.data === undefined) return;
        broadcastQueue.scheduleBroadcast(queryKey, state.data, state.dataUpdatedAt);
      }
    } : undefined;
    // FR13 fix #1 — install the devtool QueryCache subscriber so
    // cache:miss + cache:write events flow into sdkEvents. No-op in
    // production builds (gated by __MORFI_DEVTOOL_ENABLED__ flag inside
    // installDevtool — DCE-stripped when false).
    const disposeDevtool = installDevtool(qc);
    teardowns.push(disposeDevtool);
    const projection = installConvictionProjector(qc, {
      managedListener: true,
      appState: adapter.appState,
      isRowActive: row => {
        const pubkey = row.pubkey;
        return typeof pubkey === 'string' && getRegistrySingleton(qc).isEntityVisible(pubkey);
      }
    });
    teardowns.push(projection);
    const unifiedDispose = createUnifiedQueryCacheListener({
      onProjectionEvent: projection.onCacheEvent,
      reverseIndex: reverseIndexHandlers,
      pendingListKeys: pendingListHandlers,
      onQueryRemoved: event_1 => {
        clearLiveRows(qc, hashKey(event_1.query.queryKey));
      },
      onSessionFetchEvent: recordSessionFetchEvent,
      // exactOptionalPropertyTypes: omit the key entirely when unsupported.
      ...(broadcastOut !== undefined ? {
        broadcastOut
      } : {})
    }).attach(qc);
    teardowns.push(unifiedDispose);
    teardowns.push(() => cacheBinding.dispose());
    teardowns.push(() => broadcastQueue.dispose());
    // FR1 fix #1 — mount the domain-grace sweep loop so released domains
    // are torn down 2s after release (instead of relying solely on
    // FIFO-at-cap eviction). The sweep is registry-internal (one
    // setInterval per registry) so it lives outside `teardowns`.
    const graceHandle = getRegistrySingleton(qc).startGraceSweep();
    teardowns.push(() => graceHandle.stop());
    // Clear the non-React active-client handle on a REAL unmount so surfaces
    // outside the subtree don't write to a dead client. Deferred + guarded so
    // StrictMode's dev mount→unmount→remount does not strand the global at
    // `undefined`: this setup re-runs on the re-setup pass and flips the flag
    // back to true before the microtask fires, so the fake unmount is skipped.
    activeClientAliveRef.current = true;
    setActiveQueryClient(qc);
    teardowns.push(() => {
      activeClientAliveRef.current = false;
      queueMicrotask(() => {
        if (!activeClientAliveRef.current && getActiveQueryClient() === qc) {
          setActiveQueryClient(undefined);
        }
      });
    });
    // OTel telemetry — installs a single subscriber that translates every
    // `morfi.*` event from `sdkEvents` into OTLP/JSON envelopes and ships
    // them to Better Stack (or any OTLP backend). Skipped when the host
    // doesn't pass `telemetry`. See TELEMETRY.md for the integration
    // contract; the install function itself enforces SSR + double-install
    // + HMR + opt-out guards.
    if (telemetry !== undefined) {
      const disposeTelemetry = installOtelTelemetry(qc, {
        ...telemetry,
        appState: adapter.appState
      });
      teardowns.push(disposeTelemetry);
    }
    // (cross-tab cache:write sync is wired above, before the unified
    // QueryCache listener, so its `broadcastOut` handler can ride the
    // single subscription. Snapshot-piggyback was removed — it had no
    // bulk-snapshot producer to emit from.)
    // B3 — mutation-default persistence sentinels.
    installPersistenceSentinels(qc);
    // B3b — degraded-poll resume catch-up (T17307). On app/tab
    // foreground-return while in DEGRADED_NO_NATS, fire ONE catch-up
    // fetch for overdue polling queries instead of waiting a full
    // interval (TanStack's function-form refetchInterval then resumes
    // the normal cadence). Closes the "stale until next tick" gap.
    teardowns.push(installDegradedPollResume(qc, adapter.appState));
    // B5 — pagehide / freeze flush listeners (web only; RN handled via AppState).
    // Phase 5 will route this through `adapter.appState`. For now the
    // legacy installer is gated on `adapter.kind` so RN consumers don't
    // hit web-only listener installs.
    let bootDisposed = false;
    teardowns.push(() => {
      bootDisposed = true;
    });
    if (adapter.kind === 'web' && runtimeBase.persister !== null) {
      // B2 + B4 — strict 5-phase boot order per spec §L2605.
      //
      // ARCHITECTURAL NOTE: we do NOT call `runSDKBootSequence` directly
      // because that function deliberately omits the `hydrate(qc, state)`
      // step (it's a test-seam that pins phase ORDER, not the runtime).
      // The phase sequence below matches `runSDKBootSequence` 1:1 with the
      // hydrate baked in between restore + sentinel install.
      //
      //   1. create-client       (done by `useState(() => new QueryClient())`)
      //   2. register-mutation-defaults  (no codegen specs yet — empty)
      //   3. persist-restore (maxAge/buster gate) + hydrate
      //   4. install-sentinels   (Paused + Inflight)
      //   5. resumePausedMutations
      const persister = runtimeBase.persister;
      const inflight = runtimeBase.inflightMap;
      void (async () => {
        try {
          // Phase 3 — restore. Replicate TanStack persistQueryClientRestore's
          // maxAge + buster gate (that helper is not a dependency here): drop a
          // blob with no timestamp, older than maxAge, or written under a
          // different schema buster, so a stale snapshot never rehydrates.
          const persisted = await persister.restoreClient();
          if (bootDisposed) return;
          if (persisted !== undefined) {
            const usable = isPersistedClientUsable(persisted, {
              maxAge: PERSISTER_MAX_AGE_MS,
              buster: String(currentSchemaByte()),
              now: Date.now()
            });
            if (usable && persisted.clientState !== undefined) {
              const {
                hydrate
              } = await import('@tanstack/react-query');
              const filtered = excludeNonpersistentQueriesFromPersistedClient(persisted);
              if (!bootDisposed && filtered.clientState !== undefined) hydrate(qc, filtered.clientState);
            } else if (!usable) {
              await persister.removeClient();
            }
            // Phase 4 — install sentinels BEFORE resume. Same-scope mutate()
            // calls during drain `await` the sentinel via the inflight Map.
            const cache = qc.getMutationCache();
            installPausedMutationSentinels({
              getAll: () => cache.getAll().filter(m_0 => (m_0.options.mutationKey?.length ?? 0) > 0).map(m_1 => ({
                state: {
                  isPaused: m_1.state.isPaused
                },
                options: {
                  mutationKey: m_1.options.mutationKey
                },
                continue: () => m_1.continue()
              }))
            }, inflight);
            installInflightSentinels({
              getMutationCache: () => ({
                getAll: () => cache.getAll().map(m_2 => {
                  const scopeId = m_2.options.scope?.id;
                  return {
                    options: scopeId !== undefined ? {
                      scope: {
                        id: scopeId
                      }
                    } : {},
                    state: {
                      isPaused: m_2.state.isPaused,
                      status: m_2.state.status
                    }
                  };
                }),
                subscribe: listener => cache.subscribe(event_2 => {
                  const m_3 = event_2.mutation;
                  if (m_3 === undefined) return;
                  const scopeId_0 = m_3.options.scope?.id;
                  listener({
                    type: event_2.type,
                    mutation: {
                      options: scopeId_0 !== undefined ? {
                        scope: {
                          id: scopeId_0
                        }
                      } : {},
                      state: {
                        isPaused: m_3.state.isPaused,
                        status: m_3.state.status
                      }
                    }
                  });
                })
              })
            }, {
              get: k => inflight.get(k),
              set: (k_0, v) => inflight.set(k_0, v),
              delete: k_1 => inflight.delete(k_1)
            });
            // Phase 5 — resume.
            await qc.resumePausedMutations();
          }
        } catch (err_8) {
          // FR9 fix #4 · restore failures are non-fatal (cold start still
          // works) but they MUST be visible to engineers. Surfaced via
          // console.warn so a broken persister doesn't fail silently.
          console.warn('[sdk] persist-restore failed; cold-starting', err_8);
          sdkEvents.emit('cache:diagnostic', {
            kind: 'persistence',
            reason: 'restore-failed'
          });
        } finally {
          if (!bootDisposed) {
            setPersistenceRestored(true);
            let storageFailureReported = false;
            const save = () => {
              const snapshot = {
                buster: String(currentSchemaByte()),
                timestamp: Date.now(),
                clientState: dehydrate(qc, {
                  shouldDehydrateQuery
                })
              };
              void Promise.resolve().then(() => persister.persistClient(snapshot)).catch(() => {
                if (storageFailureReported) return;
                storageFailureReported = true;
                sdkEvents.emit('cache:diagnostic', {
                  kind: 'persistence',
                  reason: 'write-failed-memory-retained'
                });
              });
            };
            teardowns.push(qc.getQueryCache().subscribe(event => {
              if (event.type === 'updated' && event.action.type === 'success' && shouldDehydrateQuery(event.query)) save();
            }));
            teardowns.push(adapter.appState.addListener(event => {
              if (event.kind === 'hidden' || event.kind === 'freeze') save();
            }));
            if (adapter.appState.addPageHideListener !== undefined) teardowns.push(adapter.appState.addPageHideListener(save));
            teardowns.push(save);
          }
        }
      })();
    } else {
      setPersistenceRestored(true);
    }
    return () => {
      for (const t of teardowns.reverse()) {
        try {
          t();
        } catch {
          /* teardown must not throw */
        }
      }
    };
    // Boot exactly once per provider mount. qc + runtimeBase + extractor
    // are useState-initialised refs and never change.
  }, [telemetry, runtimeBase.tabNonce, runtimeBase.reverseIndex.removeKeyForId, runtimeBase.persister, runtimeBase.inflightMap, qc.resumePausedMutations, qc, adapter.kind, adapter.broadcast, apiUrl, networkScope, extractIdsFromKey, adapter.storage.sync, adapter.appState]);
  // Drive TanStack's `onlineManager` from the SDK's connectivity FSM (web).
  //
  // Mutations default to `networkMode: 'online'`, so TanStack pauses every
  // write whenever `onlineManager` reports offline. On web TanStack's default
  // listener follows raw `navigator.onLine` + `window` online/offline events —
  // an unreliable signal that, on a long-lived session, can stick `false`
  // (a spurious `offline` event whose recovery `online` is missed, or a lying
  // `navigator.onLine`). When that happens the FSM still reports CONNECTED via
  // its active REST/NATS probing, but TanStack silently traps writes after
  // their optimistic patch (`onMutate` runs, the `fetch` phase never does), and
  // a later refetch rolls the optimism back — the "bookmark fills then reverts,
  // nothing saves" bug. The FSM is the SDK's authoritative liveness signal, and
  // its own spec ties states to write flushing (CONNECTED/DEGRADED_NO_NATS =
  // REST healthy → writes flush; DEGRADED_NO_REST/OFFLINE = no writes). Wiring
  // `onlineManager` to it keeps offline write pause/persist/resume working while
  // eliminating the false-offline trap. RN already wires `onlineManager` to its
  // netInfo slot in its own provider, so gate this to web.
  useEffect(() => {
    if (adapter.kind !== 'web') return;
    const restHealthy = state_0 => state_0 === 'CONNECTED' || state_0 === 'DEGRADED_NO_NATS';
    onlineManager.setEventListener(setOnline => {
      setOnline(restHealthy(getConnectivitySnapshot().state));
      return subscribeConnectivity(snap => setOnline(restHealthy(snap.state)));
    });
  }, [adapter.kind]);
  // Pre-client orchestrator — a first connect that keeps failing has no
  // client, so the full orchestrator below never installs and the FSM would
  // read CONNECTED (no degraded polling) while the ladder retries. Fold the
  // connect-health signal and the REST online/offline signal into the FSM
  // now, with no heartbeat (there is no socket to hear) and no reconnect
  // ladder of its own (the cold ladder + pre-client edges own retries). The
  // full orchestrator replaces it as soon as a client lands; it seeds from
  // the store, so the DEGRADED→CONNECTED edge still emits.
  useEffect(() => {
    if (clientOverride !== null || natsUrl === '' || client_0 !== null) return;
    const stopOrchestrator = installConnectivityOrchestrator({
      qc,
      installNatsHeartbeat: () => () => {},
      installOnlineOfflineHandler: ({
        setRestHealthy
      }) => installOnlineOfflineHandler({
        netInfo: {
          subscribe: h => adapter.netInfo.addListener(h)
        },
        setRestHealthy,
        runReconnectLadder: async () => {},
        probeRest: () => probeRestConnectivity(apiUrl)
      }),
      emitToBus: t_0 => sdkEvents.emit('connectivity:change', t_0)
    });
    return stopOrchestrator;
  }, [adapter, apiUrl, client_0, clientOverride, natsUrl, qc]);
  // Connectivity orchestrator — wires NATS heartbeat + REST online/offline
  // through hysteresis into the 4-state FSM. Runs once the NATS client is
  // available; the FSM snapshot survives remounts because the store is a
  // module-level singleton (`connectivityStore`).
  //
  // Probe the API's public liveness route before resuming REST work after
  // an online edge. The same-origin development proxy keeps its root probe.
  useEffect(() => {
    // Runs on BOTH web and RN: `connectivity/*` is platform-neutral and the
    // signals it consumes (NATS status, netInfo, appState) are adapter
    // slots both platforms implement. Gate only on a live NATS client.
    if (client_0 === null) return;
    // Bridge the platform-adapter `NetInfoSlot.addListener` shape into
    // `NetInfoAdapter.subscribe` so `installOnlineOfflineHandler` (Phase 5
    // refactor target) can consume it without further change.
    const netInfoBridge = {
      subscribe: h_0 => adapter.netInfo.addListener(h_0)
    };
    const stopOrchestrator_0 = installConnectivityOrchestrator({
      qc,
      installNatsHeartbeat: ({
        setNatsHealthy,
        recordTick,
        onReconnect
      }) => installNatsHeartbeat({
        statusIterator: () => wrapStatusForOutageSim(client_0.status()),
        setNatsHealthy,
        recordTick,
        onReconnect
      }),
      installOnlineOfflineHandler: ({
        setRestHealthy: setRestHealthy_0
      }) => installOnlineOfflineHandler({
        netInfo: netInfoBridge,
        setRestHealthy: setRestHealthy_0,
        runReconnectLadder: async () => {
          // Network just came back (after `navigator offline`): re-mint a
          // fresh NATS jwt + reconnect, THEN refetch realtime queries. Bare
          // nats-core reconnect re-uses the stale store jwt and can hang on
          // mobile after airplane mode — this restores realtime without a
          // page refresh.
          void runReconnectWithRemintRef.current('online');
          await refetchRecoverySnapshots(qc);
        },
        probeRest: () => probeRestConnectivity(apiUrl)
      }),
      emitToBus: t_1 => sdkEvents.emit('connectivity:change', t_1),
      onNatsReconnect: () => getRegistrySingleton(qc).notifyWireConsumerRecreated()
    });
    // Advance the per-query poll ladder on realtime fetch success, not manual patches
    // (so successive identical responses ramp 4 s → 8 s → 16 s → 32 s).
    const unsubCache = qc.getQueryCache().subscribe(ev_0 => {
      if (ev_0.type !== 'updated') return;
      if (ev_0.query.meta?.realtime !== true) return;
      if (ev_0.action.type !== 'success' || ev_0.action.manual === true) return;
      notePollResponse(ev_0.query, ev_0.query.state.data);
    });
    // Reset the per-query poll ladder back to the 4 s base every time the
    // FSM transitions to CONNECTED — the next DEGRADED episode starts
    // fresh. The poll itself is driven by TanStack's function-form
    // `refetchInterval` set in `realtimeQueryOptions`; each realtime hook
    // subscribes to `connectivityStore.state` via `useSyncExternalStore`,
    // so a transition triggers a re-render → `observer.setOptions` →
    // `refetchInterval` re-evaluates with the new state.
    const unsubBus = sdkEvents.on('connectivity:change', payload => {
      const t_2 = payload;
      if (t_2.to !== 'CONNECTED') return;
      for (const q of qc.getQueryCache().findAll()) {
        if (q.meta?.realtime === true) resetPollState(q);
      }
    });
    // Page-lifecycle handlers — pure decision logic lives in
    // `connectivity/lifecycle/pings.ts`. The provider routes
    // `AppStateEvent` (delivered by `adapter.appState`) into the legacy
    // visibilitychange / pageshow shapes those handlers expect.
    const applyLifecycleAction = action => {
      if (action.kind === 'noop' || action.kind === 'pause-dispatch') return;
      if (action.kind === 'force-reconnect') {
        // Re-mint + reconnect (not a bare `client.reconnect()` which re-uses
        // the stale store jwt) so a backgrounded mobile tab whose token
        // rotated/expired recovers without a page refresh.
        void runReconnectWithRemintRef.current(`resume:${action.cause}`);
        return;
      }
      // resume-and-probe — flush the WS within the timeout; on success
      // the heartbeat will mark NATS healthy. On failure, escalate to a
      // re-mint + reconnect.
      void (async () => {
        const {
          alive
        } = await pingAndVerify({
          flush: async () => client_0.flush()
        }, {
          timeoutMs: action.pongTimeoutMs
        });
        if (!alive) void runReconnectWithRemintRef.current('resume:pong-timeout');
      })();
    };
    const unsubAppState = adapter.appState.addListener(ev_1 => {
      if (ev_1.kind === 'visible' || ev_1.kind === 'hidden') {
        applyLifecycleAction(handleVisibilityChange({
          visibility: ev_1.kind
        }));
        return;
      }
      if (ev_1.kind === 'bfcache-restore') {
        applyLifecycleAction(handlePageShow({
          persisted: true
        }));
        return;
      }
      // 'freeze' / 'resume' variants are no-ops for the legacy
      // pings module today; Phase 5 will route them through
      // `installPersistenceFlushListeners` etc.
    });
    return () => {
      unsubAppState();
      unsubBus();
      unsubCache();
      stopOrchestrator_0();
    };
  }, [adapter, apiUrl, client_0, qc]);
  // The SubjectRegistry singleton powers per-entity NATS subscriptions
  // inside `useRealtimeQuery`. The registry is constructed once in
  // `getRegistrySingleton()`; the context is what lets the hook reach
  // it without an import-time singleton read (improves testability).
  return _jsx(QueryClientProvider, {
    client: qc,
    children: _jsx(RegistryContext.Provider, {
      value: getRegistrySingleton(qc),
      children: _jsx(SdkRuntimeContext.Provider, {
        value: runtime,
        children: _jsx(PersistenceRestoredContext.Provider, {
          value: persistenceRestored,
          children: children
        })
      })
    })
  });
}